import React, { useMemo } from 'react';

import { Typography, Chip } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import InformationSummary from 'components/InformationSummary';
import RichTextContent from 'components/RichTextContent/RichTextContent';
import type { ActionTemplate } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

import ActionTemplateDetailsSteps from './_components/ActionTemplateDetailsSteps';
import useStyles from './ActionTemplateDetails.styles';

type Props = {
  template: ActionTemplate;
};

const ActionTemplateDetails: React.FC<Props> = ({ template }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const validityPeriod = useMemo(() => {
    if (template.validityPeriodMonths) {
      return `${template.validityPeriodMonths} ${template.validityPeriodMonths === 1 ? 'month' : 'months'}`;
    }
    return '-';
  }, [template.validityPeriodMonths]);

  const informationSummaryData = useMemo(() => {
    const data: Array<{ label: string[]; value: string | number | React.ReactElement | null | undefined }> = [
      {
        label: action_library_messages.table.purpose,
        value: template.purposeDisplay || template.purpose,
      },
      {
        label: action_library_messages.table.area,
        value: (template.areaData?.displayName as string) || template.areaDisplay || template.area,
      },
      {
        label: action_library_messages.table.subarea,
        value: (template.subAreaData?.displayName as string) || template.subArea || '-',
      },
      {
        label: action_library_messages.table.status,
        value: (
          <div className={styles.valueWithIcon}>
            {template.status === 'active' ? (
              <CheckCircleIcon className={`${styles.statusIcon} ${styles.activeIcon}`} />
            ) : (
              <CancelIcon className={`${styles.statusIcon} ${styles.inactiveIcon}`} />
            )}
            <span>{template.statusDisplay || t(action_library_messages.status[template.status])}</span>
          </div>
        ),
      },
      {
        label: action_library_messages.table.criticality,
        value: (() => {
          const criticalityKey = template.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
          const displayValue = template.criticalityDisplay || t(action_library_messages.criticality[criticalityKey]);
          let IconComponent;
          let iconClass;

          if (template.criticality === 'low') {
            IconComponent = InfoIcon;
            iconClass = styles.criticalityLowIcon;
          } else if (template.criticality === 'medium') {
            IconComponent = WarningIcon;
            iconClass = styles.criticalityMediumIcon;
          } else {
            IconComponent = ErrorIcon;
            iconClass = styles.criticalityHighIcon;
          }

          return (
            <div className={styles.valueWithIcon}>
              <IconComponent className={`${styles.statusIcon} ${iconClass}`} />
              <span>{displayValue}</span>
            </div>
          );
        })(),
      },
      {
        label: action_library_messages.table.creator,
        value:
          template.ownerTypeDisplay ||
          (() => {
            const creatorKey = template.ownerType === 'pocketsafe' ? 'PocketSafe' : 'Organization';
            return t(action_library_messages.creator[creatorKey]);
          })(),
      },
      {
        label: action_library_messages.detail.validity_period,
        value: validityPeriod,
      },
      {
        label: action_library_messages.detail.reported_to,
        value: template.reportedTo || '-',
      },
    ];

    if (template.ownerType === 'organization' && template.ownerOrganization) {
      data.push({
        label: action_library_messages.detail.owner_organization,
        value: template.ownerOrganization,
      });
    }

    // Add created date
    data.push({
      label: action_library_messages.detail.created_at,
      value: format(new Date(template.createdAt), 'yyyy-MM-dd HH:mm'),
    });

    // Add updated date
    if (template.updatedAt) {
      data.push({
        label: action_library_messages.detail.updated_at,
        value: format(new Date(template.updatedAt), 'yyyy-MM-dd HH:mm'),
      });
    }

    // Add created by if available
    if (template.createdBy) {
      data.push({
        label: action_library_messages.detail.created_by,
        value: template.createdBy.fullName,
      });
    }

    return data;
  }, [template, t, styles, validityPeriod]);

  return (
    <>
      <CenteredGrid gridGap={2} tight title={template.name} width='lg'>
        <InformationSummary data={informationSummaryData} />
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title={t(general_messages.short_description)} width='lg'>
        <Typography style={{ marginBottom: 8 }} variant='body2'>
          {template.shortDescription ? <RichTextContent content={template.shortDescription} /> : '-'}
        </Typography>
      </CenteredGrid>

      {template.reference && (
        <CenteredGrid gridGap={0} tight title={t(action_library_messages.detail.reference)} width='lg'>
          <div className={styles.tagsContainer}>
            {template.reference.split(', ').map((ref, index) => (
              <Chip key={index} label={ref} size='small' />
            ))}
          </div>
        </CenteredGrid>
      )}

      {template.assetCategories && template.assetCategories.length > 0 && (
        <CenteredGrid gridGap={0} tight title={t(action_library_messages.detail.asset_types)} width='lg'>
          <div className={styles.tagsContainer}>
            {template.assetCategories.map(category => (
              <Chip key={category.id} label={category.name} size='small' />
            ))}
          </div>
        </CenteredGrid>
      )}

      {template.tags && typeof template.tags === 'object' && Object.keys(template.tags).length > 0 && (
        <CenteredGrid gridGap={2} tight title={t(action_library_messages.detail.tags)} width='lg'>
          <div className={styles.tagsContainer}>
            {Object.entries(template.tags).map(([key, value]) => (
              <Chip key={key} label={`${key}: ${String(value)}`} size='small' />
            ))}
          </div>
        </CenteredGrid>
      )}

      {template.steps && template.steps.length > 0 && (
        <CenteredGrid gridGap={2} tight title={t(action_library_messages.detail.steps)} width='lg'>
          <ActionTemplateDetailsSteps steps={template.steps} />
        </CenteredGrid>
      )}
    </>
  );
};

export default ActionTemplateDetails;
